import Divider from 'components/Divider';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { Controller, useFormContext } from 'react-hook-form';
import Colors from './ColorEllipsis';
import PermissionSelection from './PermissionSelection';
import { CustomRoleFormType } from '../custom-roles.types';
import Preview from './Preview';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useNavigate } from 'react-router-dom';
import { FC } from 'react';

interface FormProps {
  withDeleteButton?: boolean;
}

const Form: FC<FormProps> = ({ withDeleteButton = false }) => {
  const {
    register,
    formState: { errors },
    control,
    watch,
  } = useFormContext<CustomRoleFormType>();

  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-1 flex-col flex-wrap justify-between overflow-hidden pt-12 xl:flex-row">
      <div className="w-full rounded-tl rounded-tr bg-background-base-surface-3 px-8 py-6 xl:max-w-[564px] xl:rounded-bl xl:rounded-tr-none">
        <p className="mb-8 text-2xl font-bold text-content-base-default">
          Role details
        </p>
        <div className="space-y-6">
          <GraniteInput
            {...register('name')}
            label="Name"
            subtitle="Role names cannot include any spaces or special characters besides hyphen and underscore."
            error={errors.name?.message}
            placeholder="E.g., Repairs only"
          />
          <GraniteInput
            {...register('description')}
            label="Description"
            subtitle="Describe the purpose of this new role."
            error={errors.description?.message}
            placeholder="Custom role for only managing repair tickets"
          />
        </div>
      </div>
      <div className="flex-1 rounded-bl rounded-br bg-background-base-surface-2 px-8 py-6 xl:rounded-bl-none  xl:rounded-tr">
        <p className="text-2xl font-bold text-content-base-default">
          Role permissions
        </p>
        <div className="mt-8 w-full">
          <p className="mb-4 font-bold text-content-base-subdued">
            Feature permissions
          </p>
          <PermissionSelection />
        </div>
        <Divider className="my-6" />
        <div>
          <p className="mb-0.5 font-bold text-content-base-subdued">
            Role identifier
          </p>
          <p className="text-sm text-content-base-subdued">
            Select a color for the role identifier tag which will be displayed
            throughout G360.
          </p>
          <Controller
            control={control}
            name="color"
            render={({ field, fieldState: { error } }) => (
              <Colors
                colors={['#7E77B3', '#A7FFB2', '#1B4A50', '#A7F4FF', '#7B7BE9']}
                selectedColor={field.value || ''}
                setSelectedColor={field.onChange}
                error={error?.message}
              />
            )}
          />
        </div>
        <Preview
          name={watch('name')}
          color={watch('color')}
          description={watch('description')}
        />
        {withDeleteButton && (
          <>
            <Divider className="my-6" />
            <div className="mb-16">
              <p className="text-2xl font-bold text-content-base-default ">
                Delete role
              </p>
              <p className="mb-2 mt-4 font-bold">
                This action cannot be undone. Once deleted, this role will be
                permanently removed.
              </p>
              <GraniteButton
                variant="destructive"
                onClick={() =>
                  navigate(`delete/${watch('name')}`, { replace: true })
                }
              >
                Delete role
              </GraniteButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Form;
